import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
    AreaChart,
    Area,
    Tooltip,
    ResponsiveContainer,
    XAxis,
    CartesianGrid,
} from "recharts";
import { getRepairs } from "../services/statisticsService"; // Ensure this fetches real repair data
import { AiOutlineCaretDown } from "react-icons/ai";

function Analytic() {
    const [repairData, setRepairData] = useState([]);
    const [view, setView] = useState("Daily");

    useEffect(() => {
        fetchRepairRevenue();
    }, [view]);

    // ✅ Fetch Repair Revenue Dynamically
    const fetchRepairRevenue = async () => {
        try {
            const repairsResponse = await getRepairs();
            let groupedData = {};

            repairsResponse.forEach((repair) => {
                if (repair.payment_status === "Completed") {
                    const repairDate = new Date(repair.repair_date);
                    let key = view === "Weekly"
                        ? `Week ${getWeekNumber(repairDate)} - ${repairDate.getFullYear()}`
                        : repairDate.toISOString().split("T")[0];

                    groupedData[key] = (groupedData[key] || 0) + parseFloat(repair.total_amount);
                }
            });

            const formattedData = Object.keys(groupedData).map((key) => ({
                date: key,
                totalRevenue: groupedData[key],
            }));

            setRepairData(formattedData);
        } catch (error) {
            console.error("Error fetching repair revenue:", error);
        }
    };

    // ✅ Get Week Number for Weekly Grouping
    const getWeekNumber = (date) => {
        const firstDay = new Date(date.getFullYear(), 0, 1);
        const dayOfYear = ((date - firstDay + 86400000) / 86400000);
        return Math.ceil(dayOfYear / 7);
    };

    // ✅ Toggle between Daily and Weekly View
    const toggleView = () => {
        setView(view === "Daily" ? "Weekly" : "Daily");
    };

    return (
        <Section>
            <div className="analytics">
                <div className="analytics__details">
                    <div>
                        <h4>Revenue from Repairs</h4>
                    </div>
                    <div>
                        <button onClick={toggleView}>
                            Switch to {view === "Daily" ? "Weekly" : "Daily"} <AiOutlineCaretDown />
                        </button>
                    </div>
                </div>

                <div className="analytics__graph">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                            data={repairData}
                            margin={{ top: 0, left: 0, right: 0, bottom: 0 }}
                        >
                            <defs>
                                <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#28a745" stopOpacity={0.8} />
                                    <stop offset="95%" stopColor="#a7f3d0" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <XAxis dataKey="date" />
                            <CartesianGrid strokeDasharray="3 3" />
                            <Tooltip cursor={{ stroke: "#28a745", strokeWidth: 2 }} />
                            <Area
                                animationBegin={800}
                                animationDuration={2000}
                                type="monotone"
                                dataKey="totalRevenue"
                                stroke="#28a745"
                                fill="url(#revenueGradient)"
                                strokeWidth={4}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </Section>
    );
}

export default Analytic;

// ✅ Styled Components
const Section = styled.section`
    .analytics {
        color: black;
        width: 100%;
        margin-top: 120px;
        padding: 1rem;
        background-color: #f7f9fc;
        border-radius: 1rem;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);

        .analytics__details {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin: 1rem 0;

            h4 {
                font-weight: bold;
                font-size: 1rem;
            }

            button {
                border-radius: 0.5rem;
                padding: 0.5rem 1rem;
                border: none;
                cursor: pointer;
                background-color: #668dff;
                color: white;
                font-weight: bold;
                transition: 0.3s ease-in-out;
                display: flex;
                align-items: center;
                gap: 0.2rem;

                &:hover {
                    background-color: #668dff;
                }

                svg {
                    font-size: 0.8rem;
                }
            }
        }

        .analytics__graph {
            height: 15rem;
            width: 100%;
            background-color: #ffffff;
            border-radius: 1rem;
            padding: 1rem;
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);

            .recharts-default-tooltip {
                background-color: #333 !important;
                border-color: #333 !important;
                color: white !important;
                border-radius: 0.5rem;
                padding: 0.5rem;
            }
        }
    }
`;
